'use client';

import React from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Ship } from 'lucide-react';
import Image from 'next/image';
import { Link } from '@/i18n/routing';
import { formatLocation } from '@/lib/location';
import UniqueVisitsIndicator from '@/components/UniqueVisitsIndicator';

export interface CruiseCardData {
  id: string;
  main_cruise_id?: string | null;
  slug?: string | null;
  name: string;
  embarkation_place?: { name?: string; place_name?: string } | string | null;
  disembarkation_place?: { name?: string; place_name?: string } | string | null;
  embarkation_date?: string | null;
  disembarkation_date?: string | null;
  hero_image_url?: string | null;
  image?: string | null;
  images?: Array<{ id?: string; url?: string; image_type?: string; order?: number }>;
  price?: number | null;
  currency?: string | null;
  price_ranges?: {
    EUR?: number;
    PLN?: number;
    USD?: number;
    [key: string]: number | undefined;
  } | null;
  cruise_time_days?: number | null;
  // Allow additional properties that might be present in API responses
  description?: string | null;
  state?: string;
  created_at?: string;
  organization_name?: string | null;
  organization_logo_url?: string | null;
  organization_slug?: string | null;
  unique_visits?: number | null;
  [key: string]: unknown;
}

interface CruiseCardAction {
  label: string;
  icon?: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'default' | 'danger' | 'warning';
  disabled?: boolean;
}

interface CruiseCardProps {
  cruise: CruiseCardData;
  variant?: 'default' | 'detailed';
  hover?: boolean;
  showStateLabel?: boolean; // Show state label (published, draft, etc.) - only for own cruises
  actions?: CruiseCardAction[]; // Action buttons to show on the card
  /** When provided, used as the link href so "Open in new tab" / middle-click work. Should match where onCardClick would navigate. */
  cardHref?: React.ComponentProps<typeof Link>['href'];
  /** Custom click handler for normal left-click only. Middle-click and Ctrl/Cmd+click use cardHref and open in new tab. */
  onCardClick?: () => void;
}

export default function CruiseCard({ cruise, variant = 'default', hover = true, showStateLabel = false, actions = [], cardHref: cardHrefProp, onCardClick }: CruiseCardProps) {
  const t = useTranslations();
  const locale = useLocale();

  // Get image URL: prefer hero_image_url, then image, then first image from images array
  const imageUrl = cruise.hero_image_url || 
    cruise.image || 
    (Array.isArray(cruise.images) && cruise.images.length > 0 ? cruise.images[0]?.url : null) ||
    null;

  // Use slug if available, otherwise fallback to main_cruise_id, then id
  const cruiseIdentifier = cruise.slug || cruise.main_cruise_id || cruise.id;
  const defaultHref: React.ComponentProps<typeof Link>['href'] = {
    pathname: '/cruises/[id]',
    params: { id: cruiseIdentifier },
  };
  const href = cardHrefProp ?? defaultHref;

  const embarkationLabel = formatLocation(cruise.embarkation_place) || 'N/A';
  const disembarkationLabel = formatLocation(cruise.disembarkation_place) || 'N/A';

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString(locale === 'pl' ? 'pl-PL' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(locale === 'pl' ? 'pl-PL' : 'en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Let middle-click, Ctrl+Click, Cmd+Click open in new tab (no preventDefault)
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.altKey) return;
    if (onCardClick) {
      e.preventDefault();
      onCardClick();
    }
  };

  return (
    <Link
      href={href}
      onClick={handleCardClick}
      className={`h-full min-h-[400px] flex flex-col cursor-pointer transition-all duration-300 bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-100 dark:border-slate-800 shadow-sm ${hover ? 'hover:-translate-y-1 hover:shadow-md dark:hover:shadow-[0_0_20px_rgba(0,0,0,0.6)]' : ''} text-slate-900 dark:text-slate-100 relative group no-underline`}
    >
      <div className="relative w-full aspect-video min-h-[180px] rounded-xl overflow-hidden flex-shrink-0">
        <UniqueVisitsIndicator variant="overlay" uniqueVisits={cruise.unique_visits} />
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={cruise.name}
            fill
            className="object-cover rounded-xl"
            unoptimized
          />
        ) : (
          <div className="h-full w-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center rounded-xl">
            <Ship size={24} className="lucide-icon text-slate-700 dark:text-slate-500" aria-hidden="true" />
          </div>
        )}
        
        {/* Action Buttons - Overlay on bottom right */}
        {actions.length > 0 && (
          <div className="absolute bottom-2 right-2 z-20 flex gap-1 flex-wrap justify-end opacity-0 group-hover:opacity-100 transition-opacity">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(e);
                }}
                disabled={action.disabled}
                className={`p-1.5 rounded-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm transition-colors shadow-md disabled:opacity-50 ${
                  action.variant === 'danger'
                    ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                    : action.variant === 'warning'
                    ? 'text-yellow-600 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-900/20'
                    : 'text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20'
                }`}
                title={action.label}
                aria-label={action.label}
              >
                {action.icon || <span className="text-xs">{action.label}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-grow flex flex-col p-4 min-h-0">
        <h3 className="text-sm font-bold leading-tight mb-2 text-inherit line-clamp-2 min-h-[2.5rem]">
          {cruise.name}
        </h3>

        <div className="flex flex-col gap-1 mb-4">
          {(cruise.embarkation_date || cruise.disembarkation_date) && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-500 dark:text-slate-400">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 002 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2zm-7 5h5v5h-5v-5z"/>
                </svg>
              </span>
              <span className="text-xs opacity-70">
                {formatDate(cruise.embarkation_date)}
                {cruise.disembarkation_date && ` - ${formatDate(cruise.disembarkation_date)}`}
              </span>
              {cruise.cruise_time_days && (
                <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] text-slate-600 dark:text-slate-400">
                  {cruise.cruise_time_days} days
                </span>
              )}
            </div>
          )}

          <div className="flex items-start gap-2 min-w-0">
            <span className="text-slate-500 dark:text-slate-400 mt-0.5">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </span>
            <span className="text-xs opacity-70 truncate max-w-full leading-snug">
              {embarkationLabel} → {disembarkationLabel}
            </span>
          </div>
        </div>

        {/* Organizer Info */}
        {(cruise.organization_name || cruise.organization_logo_url) && (
          <div className="mt-2 flex items-center gap-2 pb-2">
            {cruise.organization_logo_url && (
              <div className="relative w-6 h-6 flex-shrink-0 rounded overflow-hidden bg-slate-100 dark:bg-slate-800">
                <Image
                  src={cruise.organization_logo_url}
                  alt={cruise.organization_name || 'Organizer logo'}
                  fill
                  className="object-contain p-0.5"
                  unoptimized
                />
              </div>
            )}
            {cruise.organization_name && (
              <span className="text-xs text-slate-600 dark:text-slate-400 truncate">
                {cruise.organization_name}
              </span>
            )}
          </div>
        )}

        {/* State Label - Only shown when showStateLabel is true (for own cruises) */}
        {showStateLabel && cruise.state && (
          <div className="mt-2 mb-2">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              cruise.state === 'published'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : cruise.state === 'ready_to_publish'
                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                : cruise.state === 'draft'
                ? 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
            }`}>
              {cruise.state === 'published' 
                ? (t('cruiseCard.state.published') || 'Published')
                : cruise.state === 'ready_to_publish'
                ? (t('cruiseCard.state.readyToPublish') || 'Ready to Publish')
                : cruise.state === 'draft'
                ? (t('cruiseCard.state.draft') || 'Draft')
                : cruise.state
              }
            </span>
          </div>
        )}

        <div className="mt-auto pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
          {cruise.price ? (
            <div className="flex items-center gap-1">
              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                {formatPrice(cruise.price)} {cruise.currency || t('cruiseView.summary.defaultCurrency') || 'EUR'}
              </span>
            </div>
          ) : (
             <div />
          )}
          <div />
        </div>

        {variant === 'detailed' && (
          <div className="mt-2">
            <span className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline">
              {t('homePage.viewDetails') || 'View Details'}
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}

